import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button, Input, Select, Table } from '@dreamtree-org/twreact-ui';
import { CalendarClock, LayoutGrid, List, Package, PackageCheck, PackageX, Pencil, Plus, Search, Trash2 } from 'lucide-react';
import { useAppApi } from '@/lib/api';
import { useCan } from '@rbac/useCan';
import Can from '@rbac/Can';
import PageHeader from '@/components/PageHeader';
import ProductImage from '@/components/ProductImage';
import ExpiryBadge from '@/components/ExpiryBadge';
import ExpiryEditor from '@/components/ExpiryEditor';
import RestockDialog from '@/components/RestockDialog';
import ConfirmDialog from '@/components/ConfirmDialog';
import { EmptyState, ErrorState, LoadingState } from '@/components/States';
import { useAppToast } from '@/lib/toast';
import { currency, date, errorMessage, number, quantity } from '@/lib/format';
import { AWAITING, DEFAULT_POLICY, expiryMeta, expiryPhrase, filterChips } from '@/lib/expiry';
import Panel from '@/components/Panel';

const VIEW_KEY = 'zorzo:products:view';

/**
 * The product list — the screen this app is really about.
 *
 * Default sort is EXPIRY, soonest first, and it is not a preference: opening
 * this page is how someone finds out what to deal with, so the most urgent row
 * is the first one without anyone choosing a sort.
 *
 * The filter state lives in the URL (`?state=critical&category=Dairy`) so a
 * dashboard tile can link straight into a filtered list, and so a clerk can
 * bookmark "what's expiring this week" or send it to someone else.
 *
 * Two views over the same rows: cards (photo-led, for walking a shelf on a
 * phone) and a table (dense, for a desk). The choice is remembered per browser.
 */
export default function ProductList() {
  const nav = useNavigate();
  const toast = useAppToast();
  const can = useCan();
  const { fetchApi } = useAppApi();
  const [params, setParams] = useSearchParams();

  const [rows, setRows] = useState([]);
  const [allCounts, setAllCounts] = useState({});
  const [policy, setPolicy] = useState(DEFAULT_POLICY);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [view, setView] = useState(() => localStorage.getItem(VIEW_KEY) || 'grid');
  const [editing, setEditing] = useState(null);
  const [restocking, setRestocking] = useState(null);
  const [awaiting, setAwaiting] = useState(0);
  const [reasons, setReasons] = useState([]);
  const [confirm, setConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // The URL is the single source of truth for every filter.
  const search = params.get('q') || '';
  const state = params.get('state') || '';
  const category = params.get('category') || '';

  const setParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  const load = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const where = {};
      if (search.trim()) where.q = search.trim();
      // "Awaiting delivery" is a status, not an expiry state — stocked-out rows
      // are excluded from the shelf list unless they are asked for by name.
      if (state === AWAITING) where.status = 'disposed';
      else if (state) where.expiry_state = state;
      if (category) where.category = category;

      // The unfiltered stats come along so the chips can show how many rows
      // each state holds — a filter that reports "0" before you click it.
      const [list, stats] = await Promise.all([
        fetchApi('productOverview', { where }),
        fetchApi('productStats'),
      ]);
      setRows((list && list.data) || []);
      if (list && list.policy) setPolicy(list.policy);
      setAllCounts((stats && stats.data && stats.data.counts) || {});
      setAwaiting((stats && stats.data && stats.data.awaiting_delivery) || 0);
      setCategories(((stats && stats.data && stats.data.by_category) || []).map((c) => c.category));
    } catch (ex) {
      setErr(errorMessage(ex, 'Could not load your products'));
    } finally {
      setLoading(false);
    }
    // fetchApi identity changes each render in the library hook — depend on filters only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, state, category]);

  useEffect(() => {
    const t = setTimeout(load, search ? 300 : 0); // debounce typing, run filters immediately
    return () => clearTimeout(t);
  }, [load, search]);

  // Served rather than hardcoded, so a reason added on the server shows up here
  // without a client rebuild.
  useEffect(() => {
    let active = true;
    axios
      .get('/api/meta/vocab', { withCredentials: true })
      .then((res) => active && setReasons((res.data && res.data.closeReasons) || []))
      .catch(() => {
        /* the dialog falls back to its own list */
      });
    return () => {
      active = false;
    };
  }, []);

  const chooseView = (v) => {
    setView(v);
    try {
      localStorage.setItem(VIEW_KEY, v);
    } catch {
      /* a private window just forgets the choice */
    }
  };

  /** One row changed — patch it in place rather than refetching the whole list. */
  const applySaved = (saved) => {
    if (!saved) return load();
    setRows((current) => current.map((r) => (r.id === saved.id ? { ...r, ...saved } : r)));
  };

  const remove = async () => {
    if (!confirm) return;
    setDeleting(true);
    try {
      await fetchApi('crud', { model: 'Product', action: 'delete', where: { id: confirm.id } });
      toast.success('Product deleted', `${confirm.name} was removed from the warehouse.`);
      setConfirm(null);
      load();
    } catch (ex) {
      toast.error('Could not delete', errorMessage(ex));
    } finally {
      setDeleting(false);
    }
  };

  const chips = useMemo(() => filterChips(allCounts, awaiting), [allCounts, awaiting]);

  const columns = useMemo(
    () => [
      {
        key: 'name',
        label: 'Product',
        sortable: true,
        render: (r) => (
          <button onClick={() => nav(`/products/${r.id}`)} className="flex items-center gap-3 text-left">
            <ProductImage src={r.image_url} name={r.name} className="h-10 w-10 shrink-0" rounded="rounded-lg" />
            <span className="min-w-0">
              <span className="block truncate font-medium text-primary-600 hover:underline">{r.name}</span>
              <span className="block truncate text-xs text-[var(--app-muted)]">
                {r.sku}
                {r.brand ? ` · ${r.brand}` : ''}
              </span>
            </span>
          </button>
        ),
      },
      { key: 'category', label: 'Category', sortable: true, render: (r) => r.category || '—' },
      { key: 'batch_no', label: 'Batch', sortable: true, render: (r) => r.batch_no || '—' },
      {
        key: 'quantity',
        label: 'Qty',
        sortable: true,
        render: (r) => <span className="nums">{quantity(r.quantity, r.unit)}</span>,
      },
      {
        key: 'expiry_date',
        label: 'Expires',
        sortable: true,
        render: (r) => (
          <span className="nums whitespace-nowrap">{r.expiry_date ? date(r.expiry_date) : '—'}</span>
        ),
      },
      {
        key: 'days_to_expiry',
        label: 'Status',
        sortable: true,
        render: (r) => <ExpiryBadge state={r.expiry_state} days={r.days_to_expiry} />,
      },
      {
        key: 'value',
        label: 'Value',
        sortable: true,
        render: (r) => <span className="nums">{currency(Number(r.quantity || 0) * Number(r.unit_price || 0))}</span>,
      },
    ],
    [nav]
  );

  const actions = (row) =>
    [
      { key: 'view', label: 'View', onClick: () => nav(`/products/${row.id}`) },
      can('inventory', 'update') && {
        key: 'expiry',
        label: 'Update expiry',
        onClick: () => setEditing(row),
      },
      can('inventory', 'update') && {
        key: 'restock',
        label: row.status === 'disposed' ? 'Record delivery' : 'Stock out & reorder',
        onClick: () => setRestocking(row),
      },
      can('inventory', 'update') && { key: 'edit', label: 'Edit', onClick: () => nav(`/products/${row.id}/edit`) },
      can('inventory', 'delete') && {
        key: 'delete',
        label: 'Delete',
        destructive: true,
        onClick: () => setConfirm(row),
      },
    ].filter(Boolean);

  return (
    <div className="space-y-5">
      <PageHeader
        title="Products"
        subtitle="Every batch in the warehouse, soonest to expire first."
        actions={
          <div className="flex items-center gap-2">
            <div className="flex rounded-lg border border-[var(--app-border)] p-0.5">
              {[
                ['grid', LayoutGrid, 'Card view'],
                ['table', List, 'Table view'],
              ].map(([key, IconC, title]) => (
                <button
                  key={key}
                  onClick={() => chooseView(key)}
                  title={title}
                  aria-label={title}
                  aria-pressed={view === key}
                  className={`rounded-md p-1.5 transition-colors ${
                    view === key ? 'bg-primary-600 text-white' : 'text-[var(--app-muted)] hover:text-primary-600'
                  }`}
                >
                  <IconC size={16} />
                </button>
              ))}
            </div>
            <Can module="inventory" action="create">
              <Button leftIcon={<Plus size={16} />} onClick={() => nav('/products/new')}>
                Add product
              </Button>
            </Can>
          </div>
        }
      />

      <Panel className="space-y-3 p-4">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <Input
            placeholder="Search name, SKU, brand or batch"
            value={search}
            onChange={(e) => setParam('q', e.target.value)}
            icon={<Search size={16} />}
            iconPosition="left"
            clearable
            onClear={() => setParam('q', '')}
          />
          <Select
            options={[{ value: '', label: 'All categories' }, ...categories.map((c) => ({ value: c, label: c }))]}
            value={category}
            onChange={(v) => setParam('category', v || '')}
            placeholder="All categories"
          />
          <Button variant="outline" onClick={load}>
            Refresh
          </Button>
        </div>

        {/* The expiry filter, as chips rather than a dropdown: the counts are
            the point, and a dropdown hides them until it is opened. */}
        <div className="flex flex-wrap gap-1.5">
          {chips.map((chip) => {
            const active = state === chip.value;
            return (
              <button
                key={chip.value || 'all'}
                onClick={() => setParam('state', chip.value)}
                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-all ${
                  active
                    ? 'border-primary-600 bg-primary-600 text-white'
                    : 'border-[var(--app-border)] bg-[var(--app-surface)] text-[var(--app-text)] hover:border-primary-400'
                }`}
              >
                {chip.solid && !active ? (
                  <span className="h-2 w-2 rounded-full" style={{ background: chip.solid }} />
                ) : null}
                {chip.label}
                {chip.count != null ? (
                  <span className={`nums ${active ? 'text-primary-100' : 'text-[var(--app-muted)]'}`}>{chip.count}</span>
                ) : null}
              </button>
            );
          })}
        </div>
      </Panel>

      {loading ? <LoadingState rows={6} /> : null}
      {!loading && err ? <ErrorState message={err} onRetry={load} /> : null}

      {!loading && !err ? (
        <div className="flex items-center justify-between px-1 text-sm text-[var(--app-muted)]">
          <span>
            <span className="nums font-semibold text-[var(--app-text)]">{number(rows.length)}</span>{' '}
            {rows.length === 1 ? 'product' : 'products'}
            {state || category || search ? ' matching your filters' : ' in the warehouse'}
          </span>
          {state || category || search ? (
            <button onClick={() => setParams({}, { replace: true })} className="font-medium text-primary-600 hover:underline">
              Clear filters
            </button>
          ) : null}
        </div>
      ) : null}

      {!loading && !err && rows.length === 0 ? (
        <EmptyState
          icon={state || category || search ? <PackageX size={40} /> : <Package size={40} />}
          title={
            state === AWAITING
              ? 'Nothing is awaiting delivery'
              : state || category || search
                ? 'Nothing matches those filters'
                : 'No products yet'
          }
          message={
            state === AWAITING
              ? 'Everything that has been stocked out has had its delivery recorded.'
              : state || category || search
                ? 'Try a different search, or clear the filters to see the whole warehouse.'
                : 'Add your first product to start tracking what expires when.'
          }
          action={
            state || category || search ? (
              <Button variant="outline" onClick={() => setParams({}, { replace: true })}>
                Clear filters
              </Button>
            ) : (
              <Can module="inventory" action="create">
                <Button leftIcon={<Plus size={16} />} onClick={() => nav('/products/new')}>
                  Add product
                </Button>
              </Can>
            )
          }
        />
      ) : null}

      {!loading && !err && rows.length > 0 && view === 'table' ? (
        <Panel className="p-2 sm:p-4">
          <Table
            data={rows}
            columns={columns}
            pagination
            pageSize={15}
            showSerial={false}
            withAction
            replaceDefaultActions
            actions={actions}
            onRowClick={(r) => nav(`/products/${r.id}`)}
          />
        </Panel>
      ) : null}

      {!loading && !err && rows.length > 0 && view === 'grid' ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
          {rows.map((p, i) => {
            const meta = expiryMeta(p.expiry_state);
            return (
              <Panel
                key={p.id}
                className="zz-card-in group overflow-hidden p-0 transition-shadow hover:shadow-lg"
                style={{
                  borderTop: `3px solid ${meta.solid}`,
                  // Stagger only the first screenful: past that the delay would
                  // be a wait, not a flourish.
                  animationDelay: `${Math.min(i, 12) * 30}ms`,
                }}
              >
                <button onClick={() => nav(`/products/${p.id}`)} className="block w-full text-left">
                  <div className="relative">
                    <ProductImage
                      src={p.image_url}
                      name={p.name}
                      className="h-40 w-full transition-transform duration-500 group-hover:scale-[1.03]"
                      rounded="rounded-none"
                    />
                    <div className="absolute left-2 top-2">
                      <ExpiryBadge state={p.expiry_state} days={p.days_to_expiry} size="xs" />
                    </div>
                    {p.category ? (
                      <div className="absolute right-2 top-2 rounded-full bg-black/55 px-2 py-0.5 text-[11px] font-medium text-white backdrop-blur-sm">
                        {p.category}
                      </div>
                    ) : null}
                  </div>

                  <div className="p-3">
                    <div className="truncate font-semibold text-[var(--app-text)]">{p.name}</div>
                    <div className="truncate text-xs text-[var(--app-muted)]">
                      {p.sku}
                      {p.brand ? ` · ${p.brand}` : ''}
                    </div>
                    <div className="mt-2 flex items-baseline justify-between gap-2">
                      <span className="nums text-sm font-medium" style={{ color: meta.fg }}>
                        {expiryPhrase(p.days_to_expiry)}
                      </span>
                      <span className="nums shrink-0 text-xs text-[var(--app-muted)]">
                        {quantity(p.quantity, p.unit)}
                      </span>
                    </div>
                  </div>
                </button>

                <div className="flex items-center justify-between gap-2 border-t border-[var(--app-border)] bg-[var(--app-surface-2)] px-3 py-2">
                  <span className="nums text-xs text-[var(--app-muted)]">
                    {p.expiry_date ? date(p.expiry_date) : 'No date set'}
                  </span>
                  <div className="flex items-center gap-1">
                    <Can module="inventory" action="update">
                      <button
                        onClick={() => setEditing(p)}
                        title="Update expiry date"
                        aria-label={`Update expiry date for ${p.name}`}
                        className="rounded-md p-1.5 text-[var(--app-muted)] transition-colors hover:bg-primary-50 hover:text-primary-600"
                      >
                        <CalendarClock size={16} />
                      </button>
                      <button
                        onClick={() => setRestocking(p)}
                        title={p.status === 'disposed' ? 'Record delivery' : 'Stock out & reorder'}
                        aria-label={`Stock out and reorder ${p.name}`}
                        className="rounded-md p-1.5 text-[var(--app-muted)] transition-colors hover:bg-primary-50 hover:text-primary-600"
                      >
                        <PackageCheck size={16} />
                      </button>
                      <button
                        onClick={() => nav(`/products/${p.id}/edit`)}
                        title="Edit product"
                        aria-label={`Edit ${p.name}`}
                        className="rounded-md p-1.5 text-[var(--app-muted)] transition-colors hover:bg-primary-50 hover:text-primary-600"
                      >
                        <Pencil size={16} />
                      </button>
                    </Can>
                    <Can module="inventory" action="delete">
                      <button
                        onClick={() => setConfirm(p)}
                        title="Delete product"
                        aria-label={`Delete ${p.name}`}
                        className="rounded-md p-1.5 text-[var(--app-muted)] transition-colors hover:bg-error-50 hover:text-error-600"
                      >
                        <Trash2 size={16} />
                      </button>
                    </Can>
                  </div>
                </div>
              </Panel>
            );
          })}
        </div>
      ) : null}

      <RestockDialog
        product={restocking}
        policy={policy}
        reasons={reasons}
        isOpen={!!restocking}
        onClose={() => setRestocking(null)}
        onSaved={load}
      />

      <ExpiryEditor
        product={editing}
        policy={policy}
        isOpen={!!editing}
        onClose={() => setEditing(null)}
        onSaved={applySaved}
      />

      <ConfirmDialog
        isOpen={!!confirm}
        title="Delete this product?"
        confirmLabel="Delete product"
        loading={deleting}
        onConfirm={remove}
        onClose={() => setConfirm(null)}
        message={
          confirm
            ? `"${confirm.name}" (${confirm.sku}) and its photo will be removed. This cannot be undone.`
            : ''
        }
      />
    </div>
  );
}
